<script>
  import { apiFetch } from '../lib/auth.js';
  import { onMount, tick } from 'svelte';
  import ReviewForm from '../components/ReviewForm.svelte';
  import DatePicker from '../components/DatePicker.svelte';

  export let loggedIn = false;

  // ── Data ──────────────────────────────────────────────────────────────────
  let performances = [];
  let loading      = false;
  let error        = '';

  // ── Filters ───────────────────────────────────────────────────────────────
  let timeWindow  = 'upcoming'; // 'upcoming' | 'range' | 'day' | 'all'
  let dateFrom    = '';
  let dateTo      = '';
  let dateDay     = '';
  let genreFilter = '';
  let genres      = [];
  let eventFilter = '';
  let venueFilter = '';
  let myListOnly  = false;
  let artistSearch = '';
  let searchDebounce;

  // ── Follows ───────────────────────────────────────────────────────────────
  let followedSet = new Set();

  // ── Expand ────────────────────────────────────────────────────────────────
  let loggingPerf = null;
  let expandedEl  = null;

  // ── Init ──────────────────────────────────────────────────────────────────
  onMount(() => {
    Promise.all([loadGenres(), loadFollows()]).then(loadPerformances);
  });

  async function loadGenres() {
    try { genres = await apiFetch('/artists/genres') || []; } catch { genres = []; }
  }
  async function loadFollows() {
    if (!loggedIn) return;
    try {
      const data = await apiFetch('/follows');
      followedSet = new Set(Array.isArray(data) ? data.map(f => `performance:${f.entity_id}`) : []);
    } catch { followedSet = new Set(); }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function pad(n) { return String(n).padStart(2, '0'); }
  function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }

  // ── Load — only time window ('upcoming' vs 'all') goes to the server.
  //    Everything else (date range, genre, venue, event, myList) is client-side
  //    so all filters are reactive and instant with no extra fetches.
  function buildParams() {
    const p = new URLSearchParams();
    if (timeWindow === 'all') p.set('from', 'all');
    return p;
  }

  async function loadPerformances() {
    loading = true; error = '';
    try {
      const data = await apiFetch(`/performances/upcoming?${buildParams()}`);
      performances = Array.isArray(data) ? data : [];
    } catch (e) { error = e.message || 'Failed to load'; }
    finally { loading = false; }
  }

  // ── Filter actions ────────────────────────────────────────────────────────
  function setWindow(w) {
    timeWindow = w; loggingPerf = null;
    if (w === 'range' && !dateFrom) dateFrom = todayIso();
    if (w === 'day'   && !dateDay)  dateDay  = todayIso();
    loadPerformances();
  }
  function setGenre(g)  { genreFilter = g; loggingPerf = null; }
  function setEvent(id) { eventFilter = id ? parseInt(id) : ''; loggingPerf = null; }
  function setVenue(id) { venueFilter = id ? parseInt(id) : ''; loggingPerf = null; }
  function toggleMyList() { myListOnly = !myListOnly; }
  function onSearchInput() {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => { loggingPerf = null; }, 150);
  }

  // ── Day-step helpers ──────────────────────────────────────────────────────
  function shiftDate(iso, days) {
    const d = new Date((iso || todayIso()) + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  function stepFrom(days) { dateFrom = shiftDate(dateFrom, days); }
  function stepTo(days)   { dateTo   = shiftDate(dateTo,   days); }
  function stepDay(days)  { dateDay  = shiftDate(dateDay,  days); }

  // ── Derived ───────────────────────────────────────────────────────────────
  $: availableEvents = (() => {
    const seen = new Map();
    for (const p of performances) {
      if (p.event_id && p.event_name && !seen.has(p.event_id))
        seen.set(p.event_id, { id: p.event_id, name: p.event_name });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  $: availableVenues = (() => {
    const seen = new Map();
    for (const p of performances) {
      if (p.venue_id && p.venue_name && !seen.has(p.venue_id)) {
        seen.set(p.venue_id, { id: p.venue_id, name: p.venue_city ? `${p.venue_name}, ${p.venue_city}` : p.venue_name });
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  $: followedIds = new Set([...followedSet].map(k => parseInt(k.split(':')[1])));

  $: displayed = performances
    .filter(p => !artistSearch || p.artist_name?.toLowerCase().includes(artistSearch.toLowerCase()))
    .filter(p => !myListOnly || followedIds.has(p.id))
    .filter(p => !eventFilter || p.event_id === Number(eventFilter))
    .filter(p => !venueFilter || p.venue_id === Number(venueFilter))
    .filter(p => {
      // Genre filter (client-side; API doesn't filter this in upcoming mode)
      if (genreFilter && p.artist_genre?.length > 0)
        return p.artist_genre.includes(genreFilter);
      if (genreFilter) return false;
      return true;
    })
    .filter(p => {
      const d = p.performance_date?.slice(0, 10) ?? '';
      if (timeWindow === 'range') {
        if (dateFrom && d < dateFrom) return false;
        if (dateTo   && d > dateTo)   return false;
      }
      if (timeWindow === 'day') {
        if (dateDay && d !== dateDay) return false;
      }
      return true;
    });

  // ── Follow ────────────────────────────────────────────────────────────────
  async function toggleFollow(id, e) {
    e?.stopPropagation();
    if (!loggedIn) return;
    const key = `performance:${id}`;
    const was = followedIds.has(id);
    const next = new Set(followedSet);
    was ? next.delete(key) : next.add(key);
    followedSet = next;
    try {
      if (was) await apiFetch(`/follows/performance/${id}`, { method: 'DELETE' });
      else      await apiFetch('/follows', { method: 'POST', body: JSON.stringify({ entity_type: 'performance', entity_id: id }) });
    } catch { followedSet = followedSet; }
  }

  // ── Expand ────────────────────────────────────────────────────────────────
  async function openLog(p, e) {
    e?.stopPropagation();
    if (loggingPerf?.id === p.id) { loggingPerf = null; return; }
    loggingPerf = p;
    await tick();
    expandedEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  function closeLog() { loggingPerf = null; }
  function onLogged() {
    if (loggingPerf) { const n = new Set(followedSet); n.delete(`performance:${loggingPerf.id}`); followedSet = n; }
    loggingPerf = null;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function formatDate(d) {
    if (!d) return '—';
    const [y, m, day] = String(d).slice(0,10).split('-');
    return `${day}.${m}.${y}`;
  }
  function formatTime(t) { return t ? t.slice(0,5) : null; }
</script>

<div class="shows-page">

  <!-- ── Filter bar ── -->
  <div class="filter-bar">

    <!-- Time window pills -->
    <div class="pill-row">
      <button class="pill" class:active={timeWindow === 'upcoming'} on:click={() => setWindow('upcoming')}>Upcoming</button>
      <button class="pill" class:active={timeWindow === 'day'}      on:click={() => setWindow('day')}>Day</button>
      <button class="pill" class:active={timeWindow === 'range'}    on:click={() => setWindow('range')}>Date Range</button>

      {#if loggedIn}
        <button class="pill pill--star" class:active={myListOnly} on:click={toggleMyList} title="Show only starred">
          <svg viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          My List
        </button>
      {/if}
    </div>

    <!-- Single day picker -->
    {#if timeWindow === 'day'}
      <div class="range-row">
        <div class="range-field">
          <span class="date-label">Day</span>
          <button class="step-btn" on:click={() => stepDay(-1)} title="Previous day">‹</button>
          <DatePicker bind:value={dateDay} clearable />
          <button class="step-btn" on:click={() => stepDay(1)} title="Next day">›</button>
        </div>
      </div>
    {/if}

    <!-- Date range picker -->
    {#if timeWindow === 'range'}
      <div class="range-row">
        <div class="range-field">
          <span class="date-label">From</span>
          <button class="step-btn" on:click={() => stepFrom(-1)} title="Previous day">‹</button>
          <DatePicker bind:value={dateFrom} clearable />
          <button class="step-btn" on:click={() => stepFrom(1)} title="Next day">›</button>
        </div>
        <span class="date-sep">→</span>
        <div class="range-field">
          <span class="date-label">To</span>
          <button class="step-btn" on:click={() => stepTo(-1)} title="Previous day">‹</button>
          <DatePicker bind:value={dateTo} clearable />
          <button class="step-btn" on:click={() => stepTo(1)} title="Next day">›</button>
        </div>
      </div>
    {/if}

    <!-- Artist search -->
    <div class="search-row">
      <div class="search-box">
        <svg class="search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input class="search-input" style="padding-left:2rem" type="text" placeholder="Search artist…"
          bind:value={artistSearch} on:input={onSearchInput} />
      </div>
    </div>

    <!-- Genre / Venue / Event dropdowns -->
    {#if genres.length > 0 || availableVenues.length > 0 || availableEvents.length > 0}
      <div class="selects-row">
        {#if genres.length > 0}
          <div class="sel-wrap">
            <select class="sel" class:active={!!genreFilter} bind:value={genreFilter} on:change={() => { loggingPerf = null; }}>
              <option value="">All Genres</option>
              {#each genres as g}<option value={g}>{g}</option>{/each}
            </select>
            <svg class="sel-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        {/if}
        {#if availableVenues.length > 0}
          <div class="sel-wrap">
            <select class="sel" class:active={!!venueFilter} bind:value={venueFilter} on:change={() => { loggingPerf = null; }}>
              <option value="">All Venues</option>
              {#each availableVenues as v}<option value={v.id}>{v.name}</option>{/each}
            </select>
            <svg class="sel-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        {/if}
        {#if availableEvents.length > 0}
          <div class="sel-wrap">
            <select class="sel" class:active={!!eventFilter} bind:value={eventFilter} on:change={() => { loggingPerf = null; }}>
              <option value="">All Events</option>
              {#each availableEvents as ev}<option value={ev.id}>{ev.name}</option>{/each}
            </select>
            <svg class="sel-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        {/if}
      </div>
    {/if}

  </div>

  <!-- ── States ── -->
  {#if loading}
    <div class="state-msg"><span class="spinner"></span> Loading…</div>
  {:else if error}
    <div class="state-msg error">{error}</div>
  {:else if performances.length === 0}
    <div class="state-msg empty">No shows found.</div>
  {:else if displayed.length === 0}
    <div class="state-msg empty">No starred performances yet — star some sets.</div>
  {:else}

    <!-- ── Card list ── -->
    <div class="card-list">
      {#each displayed as p (p.id)}

        <div class="perf-card" class:is-open={loggingPerf?.id === p.id}>

          <!-- Tappable header row -->
          <div
            class="card-header"
            role={loggedIn ? 'button' : undefined}
            tabindex={loggedIn ? 0 : undefined}
            on:click={(e) => loggedIn && openLog(p, e)}
            on:keydown={e => e.key === 'Enter' && loggedIn && openLog(p, e)}
          >
            {#if loggedIn}
              <svg class="card-chev" class:open={loggingPerf?.id === p.id}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            {:else}
              <div class="card-chev-ph"></div>
            {/if}

            <div class="card-info">
              <!-- Row 1: artist + country + pip -->
              <div class="card-r1">
                <span class="card-artist">{p.artist_name}</span>
                {#if p.artist_country}<span class="card-country">({p.artist_country})</span>{/if}
                {#if p.event_type === 'festival'}
                  <span class="pip festival">{p.event_name}</span>
                {/if}
              </div>

              <!-- Row 2: genre tags -->
              {#if p.artist_genre?.length > 0}
                <div class="card-r2">
                  {#each p.artist_genre as g}
                    <span class="genre-tag">{g}</span>
                  {/each}
                </div>
              {/if}

              <!-- Row 3: time · date · venue -->
              <div class="card-r3">
                {#if p.start_time}
                  <span class="c-time">{formatTime(p.start_time)}</span>
                  <span class="c-dot">·</span>
                {/if}
                {#if p.performance_date}
                  <span class="c-date">{formatDate(p.performance_date)}</span>
                  <span class="c-dot">·</span>
                {/if}
                <span class="c-venue">{p.venue_name || '—'}{#if p.venue_city}, {p.venue_city}{/if}</span>
              </div>
            </div>

            <!-- Star — always visible, stopPropagation so it doesn't open expand -->
            {#if loggedIn}
              <button
                class="btn-star"
                class:starred={followedIds.has(p.id)}
                on:click|stopPropagation={(e) => toggleFollow(p.id, e)}
                title={followedIds.has(p.id) ? 'Unstar' : 'Star'}
              >
                <svg viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </button>
            {/if}
          </div>

          <!-- ── Expand: detail card + review form ── -->
          {#if loggingPerf?.id === p.id}
            <div class="card-expand" bind:this={expandedEl}>

              <!-- Detail summary (owned here, NOT in ReviewForm) -->
              <div class="detail-card">
                <div class="dr">
                  <span class="dl">Artist</span>
                  <span class="dv">
                    {p.artist_name}{#if p.artist_country} <span class="dv-sub">({p.artist_country})</span>{/if}
                  </span>
                </div>
                {#if p.artist_genre?.length > 0}
                  <div class="dr">
                    <span class="dl">Genre</span>
                    <span class="dv dv-tags">
                      {#each p.artist_genre as g}<span class="genre-tag">{g}</span>{/each}
                    </span>
                  </div>
                {/if}
                <div class="dr">
                  <span class="dl">Date</span>
                  <span class="dv dv-date">
                    {formatDate(p.performance_date)}
                    {#if p.start_time}<span class="dv-time">{formatTime(p.start_time)}</span>{/if}
                  </span>
                </div>
                <div class="dr">
                  <span class="dl">Venue</span>
                  <span class="dv">
                    {p.venue_name || '—'}{#if p.venue_city}, {p.venue_city}{/if}
                    {#if p.stage_name}<span class="stage-tag">{p.stage_name}</span>{/if}
                  </span>
                </div>
                {#if p.event_name}
                  <div class="dr">
                    <span class="dl">Event</span>
                    <span class="dv">
                      {p.event_name}
                      {#if p.event_type === 'festival'}<span class="pip festival" style="margin-left:0.35rem">Festival</span>{/if}
                    </span>
                  </div>
                {/if}
              </div>

              <!-- Write a Review label row -->
              <div class="expand-hdr">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                <span>Write a Review</span>
                <button class="expand-close" on:click={closeLog} title="Cancel">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <!-- ReviewForm with prefill card suppressed -->
              <ReviewForm
                mode="create"
                inline={true}
                hidePrefill={true}
                performance={p}
                on:logged={onLogged}
                on:cancel={closeLog}
              />

            </div>
          {/if}
        </div>

      {/each}
    </div>

  {/if}
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');

  /* ── Filter bar ── */
  .filter-bar { display: flex; flex-direction: column; gap: 0.55rem; margin-bottom: 1rem; }

  .pill-row { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  .pill {
    background: none; border: 1px solid #1e3d00; border-radius: 2px;
    color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.55rem; letter-spacing: 0.12em; text-transform: uppercase;
    padding: 0.3rem 0.65rem; cursor: pointer;
    transition: color 0.15s, border-color 0.15s, background 0.15s;
  }
  .pill:hover  { color: #6db800; border-color: #3a6b00; }
  .pill.active { color: #6db800; border-color: #6db800; background: rgba(109,184,0,0.08); }
  .pill--star  { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.3rem 0.6rem; }
  .pill--star svg { width: 10px; height: 10px; fill: none; }
  .pill--star.active { color: #d4a800; border-color: #d4a800; background: rgba(212,168,0,0.08); }
  .pill--star.active svg { stroke: #d4a800; fill: #d4a800; }

  /* Search bar */
  .search-row { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .search-box  { position: relative; flex: 1; min-width: 160px; display: flex; align-items: center; }
  .search-ico  { position: absolute; left: 0.55rem; width: 13px; height: 13px; color: #2a5200; pointer-events: none; flex-shrink: 0; }

  /* Date range row */
  .range-row {
    display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
    background: #060e00; border: 1px solid #1e3d00; border-radius: 2px;
    padding: 0.6rem 0.75rem;
  }
  .range-field { display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 160px; }
  .date-label {
    font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.18em; text-transform: uppercase; color: #2a5200; flex-shrink: 0;
  }
  .date-sep { color: #1e3d00; font-size: 0.75rem; flex-shrink: 0; }
  .step-btn {
    background: none; border: 1px solid #1e3d00; border-radius: 2px;
    color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 1rem; line-height: 1; padding: 0.3rem 0.45rem;
    cursor: pointer; flex-shrink: 0; transition: color 0.15s, border-color 0.15s;
  }
  .step-btn:hover { color: #6db800; border-color: #3a6b00; }

  /* Dropdowns */
  .selects-row { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .sel-wrap { position: relative; display: inline-flex; align-items: center; }
  .sel {
    appearance: none; background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #3a6b00; font-family: 'Rajdhani', sans-serif; font-size: 0.7rem; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase;
    padding: 0.4rem 1.8rem 0.4rem 0.7rem; outline: none; cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }
  .sel:hover, .sel:focus { color: #6db800; border-color: #3a6b00; }
  .sel.active { color: #6db800; border-color: #6db800; background: rgba(109,184,0,0.08); }
  .sel-arrow { position: absolute; right: 0.4rem; width: 12px; height: 12px; stroke: #2a5200; pointer-events: none; }

  /* ── States ── */
  .state-msg {
    display: flex; align-items: center; justify-content: center; gap: 0.6rem;
    padding: 3rem 0; font-family: 'Rajdhani', sans-serif; font-size: 0.7rem;
    letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200;
  }
  .state-msg.error { color: #cc6633; }
  .state-msg.empty { color: #1e3d00; }
  .spinner {
    width: 14px; height: 14px; border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Card list ── */
  .card-list {
    display: flex; flex-direction: column;
    border: 1px solid #1e3d00; border-radius: 2px; overflow: hidden;
  }

  .perf-card { border-bottom: 1px solid #0f1a00; }
  .perf-card:last-child { border-bottom: none; }
  .perf-card.is-open { background: #0a1400; }

  /* Card header — the always-visible tap target */
  .card-header {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.65rem 0.65rem 0.65rem 0.5rem;
    transition: background 0.1s;
  }
  .card-header[role="button"] { cursor: pointer; }
  .card-header[role="button"]:hover { background: #0d1a00; }
  .card-header:focus-visible { outline: 1px solid #3a6b00; outline-offset: -1px; }

  .card-chev {
    width: 15px; height: 15px; flex-shrink: 0;
    stroke: #2a5200; transition: transform 0.2s;
  }
  .card-chev.open { transform: rotate(180deg); }
  .card-chev-ph { width: 15px; flex-shrink: 0; }

  /* Info block: 3 rows stacked */
  .card-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }

  .card-r1 { display: flex; align-items: center; gap: 0.28rem; flex-wrap: wrap; }
  .card-artist  { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.95rem; color: #d4d4d4; line-height: 1.1; }
  .card-country { font-family: 'Rajdhani', sans-serif; font-size: 0.78rem; font-weight: 600; color: #3a6b00; }

  .pip {
    font-size: 0.44rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
    border: 1px solid #1e3d00; border-radius: 2px; padding: 0.05rem 0.28rem;
    color: #2a5200; font-family: 'Rajdhani', sans-serif;
  }
  .pip.festival { color: #4a7c00; border-color: #2a5200; }

  .card-r2 { display: flex; align-items: center; gap: 0.2rem; flex-wrap: wrap; }

  .genre-tag {
    font-size: 0.44rem; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase;
    border: 1px solid #1a3300; border-radius: 2px; padding: 0.05rem 0.28rem;
    color: #3a6b00; font-family: 'Rajdhani', sans-serif; display: inline-block;
  }

  .card-r3 { display: flex; align-items: center; gap: 0.28rem; flex-wrap: wrap; }
  .c-time  { font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; font-weight: 700; color: #6db800; }
  .c-date  { font-family: 'Rajdhani', sans-serif; font-size: 0.7rem; font-weight: 600; color: #4a7c00; }
  .c-dot   { color: #1e3d00; font-size: 0.55rem; }
  .c-venue { font-family: 'Rajdhani', sans-serif; font-size: 0.7rem; font-weight: 600; color: #555; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  /* Star — pinned right, always rendered */
  .btn-star {
    flex-shrink: 0; background: none; border: none; cursor: pointer;
    padding: 0.3rem; display: inline-flex; align-items: center;
  }
  .btn-star svg { width: 16px; height: 16px; stroke: #2a5200; fill: none; transition: stroke 0.15s, fill 0.15s; }
  .btn-star:hover svg { stroke: #d4a800; }
  .btn-star.starred svg { stroke: #d4a800; fill: #d4a800; }

  /* ── Expand panel ── */
  .card-expand {
    background: #060d00; border-top: 1px solid #1a3300;
    padding: 0.85rem 1rem 1.1rem 2rem;
    display: flex; flex-direction: column; gap: 0.7rem;
    animation: slideDown 0.15s ease;
  }
  @media (max-width: 480px) { .card-expand { padding: 0.75rem; } }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Detail card */
  .detail-card {
    border: 1px solid #1a3300; border-radius: 2px;
    padding: 0.55rem 0.8rem;
    display: flex; flex-direction: column; gap: 0.38rem;
  }
  .dr { display: grid; grid-template-columns: 50px 1fr; align-items: baseline; gap: 0.5rem; }
  .dl {
    font-family: 'Rajdhani', sans-serif; font-size: 0.47rem; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase; color: #3a6b00;
  }
  .dv { font-family: 'Rajdhani', sans-serif; font-size: 0.82rem; font-weight: 600; color: #c8c8c8; }
  .dv-sub  { color: #3a6b00; font-size: 0.74rem; }
  .dv-tags { display: flex; flex-wrap: wrap; gap: 0.22rem; align-items: center; }
  .dv-date { color: #6db800; }
  .dv-time { color: #3a6b00; font-size: 0.72rem; margin-left: 0.3rem; }
  .stage-tag {
    font-size: 0.47rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    border: 1px solid #1a3300; border-radius: 2px; padding: 0.05rem 0.28rem;
    color: #2a5200; font-family: 'Rajdhani', sans-serif; margin-left: 0.3rem;
    display: inline-block; vertical-align: middle;
  }

  /* Write a Review header */
  .expand-hdr {
    display: flex; align-items: center; gap: 0.5rem;
    padding-bottom: 0.55rem; border-bottom: 1px solid #1a3300;
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.6rem; letter-spacing: 0.25em; text-transform: uppercase; color: #6db800;
  }
  .expand-hdr svg { width: 13px; height: 13px; stroke: #6db800; flex-shrink: 0; }
  .expand-hdr span { flex: 1; }
  .expand-close {
    background: none; border: none; cursor: pointer; padding: 0.2rem;
    display: inline-flex; color: #2a5200; transition: color 0.15s;
  }
  .expand-close:hover { color: #cc4400; }
  .expand-close svg { width: 13px; height: 13px; }
</style>
